const API_KEY =
  process.env.REACT_APP_OPENWEATHER_KEY || "637cd51f54778ca88339846c4d0caaa7";
const BASE = "https://api.openweathermap.org/data/2.5";
const CACHE_MS = 5 * 60 * 1000;

/** @type {Map<string, { ts: number, data: object }>} */
const cache = new Map();

function hasValidCoords(lat, lng) {
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  return Number.isFinite(latNum) && Number.isFinite(lngNum);
}

export function clearWeatherCache() {
  cache.clear();
}

export function formatTempDisplay(temp, unit) {
  const n = Math.round(Number(temp) || 0);
  return unit === "imperial" ? `${n}°F` : `${n}°C`;
}

export function formatWindDisplay(wind, unit) {
  const w = Number(wind);
  if (!Number.isFinite(w)) return "—";
  return unit === "imperial" ? `${w} mph` : `${w} m/s`;
}

export function defaultWeatherSnapshot(city = "Stockholm") {
  return {
    cityLabel: city,
    temp: 12,
    description: "Partly cloudy",
    humidity: 65,
    wind: 4,
    iconCode: "02d",
    unit: "metric",
  };
}

/**
 * @param {{ lat?: string|number, lng?: string|number, city?: string, unit?: string }} opts
 */
export async function fetchWeatherSnapshot(opts = {}) {
  const unit = opts.unit === "imperial" ? "imperial" : "metric";
  const lat = opts.lat != null && opts.lat !== "" ? String(opts.lat).trim() : "";
  const lng = opts.lng != null && opts.lng !== "" ? String(opts.lng).trim() : "";
  const city = (opts.city || "").trim();
  const useCoords = hasValidCoords(lat, lng);
  const cacheKey = `${useCoords ? lat : ""}|${useCoords ? lng : ""}|${city}|${unit}`;
  const hit = cache.get(cacheKey);
  if (hit && Date.now() - hit.ts < CACHE_MS) {
    return { ...hit.data };
  }

  let url;
  if (useCoords) {
    url = `${BASE}/weather?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&units=${unit}&APPID=${API_KEY}`;
  } else if (city) {
    url = `${BASE}/weather?q=${encodeURIComponent(city)}&units=${unit}&APPID=${API_KEY}`;
  } else {
    throw new Error("City or coordinates required");
  }

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Weather API error (${res.status})`);
  }
  const data = await res.json();
  const snapshot = {
    cityLabel: data.name || city || "—",
    lat: String(data.coord?.lat ?? lat),
    lng: String(data.coord?.lon ?? lng),
    temp: Math.round(data.main?.temp ?? 0),
    description: data.weather?.[0]?.description || "—",
    humidity: data.main?.humidity ?? "—",
    wind: data.wind?.speed ?? "—",
    iconCode: data.weather?.[0]?.icon || "01d",
    unit,
  };
  cache.set(cacheKey, { ts: Date.now(), data: snapshot });
  return { ...snapshot };
}
